import {
  computed,
  ref,
  nextTick,
  onBeforeMount,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue'

import config from '../utils/config'

/**
 * @param {Object} context - The context for the component
 * @param {Object} context.props - The props for the component
 * @param {Function} context.emit - The emit function for the component
 * @param {Object} context.refs - The element references for the modal component
 * @param {String} context.type - The type of modal component to construct
 * @description A simple mixin to return properties and methods for creating modal
 * window components.
 * @returns
 */
export function modalMixin({ props, emit, refs, type }) {
  const isActive = ref(props.active || false)
  const animating = ref(true)
  const showContent = ref(false)

  const rootClass = computed(() => {
    return {
      [props.parentClass]:
        typeof props.parentClass === 'string' && props.parentClass.length > 0,
    }
  })

  /**
   * @returns {Array}
   * @description Returns the array of valid cancel options i.e
   * background ect.
   */
  const cancelOptions = computed(() => {
    return typeof props.canCancel === 'boolean'
      ? props.canCancel
        ? config.defaultModalCanCancel
        : []
      : props.canCancel
  })

  /**
   * @returns {Boolean}
   * @description Determines whether of not to show the x close
   * button in the top of the modal window
   */
  const showX = computed(() => {
    return cancelOptions.value.indexOf('x') >= 0
  })

  /**
   * @return {Boolean}
   * @description Determines whether to show the dialog cancel button
   */
  const showCancel = computed(() => {
    return cancelOptions.value.indexOf('button') >= 0
  })

  /**
   * @returns {Array}
   * @description Returns an array of classes to apply to the dialog
   */
  const dialogClasses = computed(() => {
    return {
      [`ak-modal__dialog--${props.size}`]: props.size,
      'ak-modal__dialog--full-screen': props.fullScreen,
      'ak-modal__dialog--outer-scroll': props.outerScroll,
    }
  })

  const zIndex = computed(() => {
    return `z-index: ${props.zIndex}`
  })

  /**
   * @param {Function} method
   * @description Close the Modal if canCancel and call the onCancel
   * prop (function).
   */
  const cancel = (method) => {
    if (cancelOptions.value.indexOf(method) < 0) return
    if (typeof props.onCancel === 'function') {
      props.onCancel.apply(null, arguments)
    }
    close()
  }

  /**
   * @description Call the onCancel prop (function). Emit events, and
   * destroy modal if it's programmatic.
   */
  const close = () => {
    if (props.programmatic) {
      isActive.value = false
    }

    emit('close')
    emit('update:active', false)
  }

  /**
   * @description Simple method for submitting the form
   */
  const submitHandler = (...args) => {
    try {
      if (typeof props.submitForm === 'function') {
        props.submitForm(...args)
      }
    } catch (ex) {
      console.log(ex)
    }
    close()
  }

  /**
   * @param {Event}
   * @description Keypress event that is bound to the document.
   */
  const keyPress = (event) => {
    if (isActive.value && event.keyCode === 27) {
      cancel('escape')
    }
  }

  /**
   * @description Transition after-enter hook
   */
  const afterEnter = () => {
    animating.value = false
    showContent.value = true
  }

  /**
   * @description Transition on-enter hook
   */
  const onEnter = () => {
    showContent.value = true
  }

  /**
   * @description Transition before-leave hook
   */
  const beforeLeave = () => {
    animating.value = true
    showContent.value = false
  }

  const refHasFocus = (value) => {
    return (
      refs[value] &&
      refs[value].value &&
      typeof refs[value].value.focus === 'function'
    )
  }

  const callFocus = (value) => {
    refs[value].value.focus()
  }

  onBeforeMount(() => {
    /* istanbul ignore else */
    if (typeof window !== 'undefined') {
      document.addEventListener('keyup', keyPress)
    }
  })

  onMounted(() => {
    if (props.programmatic) {
      isActive.value = true
    }

    if (props.programmatic) {
      const container = document.querySelector(props.container) || document.body

      container.style.position = 'relative'
      container.appendChild(refs.root.value)
    }

    if (type === 'dialog') {
      if (typeof props.inputAttrs.required === 'undefined') {
        props.inputAttrs.required = true
      }

      nextTick(() => {
        if (props.hasInput) {
          if (refHasFocus('input')) {
            callFocus('input')
          }
        } else if (props.focusOn === 'cancel' && props.showCancel) {
          if (refHasFocus('cancelButton')) {
            callFocus('cancelButton')
          }
        } else {
          if (refHasFocus('confirmButton')) {
            callFocus('confirmButton')
          }
        }
      })
    }
  })

  onBeforeUnmount(() => {
    /* istanbul ignore else */
    if (typeof window !== 'undefined') {
      document.removeEventListener('keyup', keyPress)
    }
  })

  watch(isActive, (value) => {
    nextTick(() => {
      if (value && refs.root.value && refs.root.value.focus) {
        refs.root.value.focus()
      }
    })
  })

  watch(
    () => props.active,
    (value) => {
      isActive.value = value
    }
  )

  return {
    isActive,
    animating,
    showContent,
    cancelOptions,
    showX,
    showCancel,
    dialogClasses,
    zIndex,
    cancel,
    close,
    submitHandler,
    keyPress,
    afterEnter,
    onEnter,
    beforeLeave,
    rootClass,
  }
}

export const modalProps = {
  /**
   * @type {String}
   * @description Whether modal is active or not, use the .sync modifier
   * to make it two-way binding
   */
  active: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {String|Function|Object}
   * @description Component to be injected, used to open a component modal
   *  programmatically. Close modal within the component by emitting a 'close'
   * event — this.$emit('close')
   */
  component: {
    type: [Object, Function, String],
    default: undefined,
  },

  /**
   * @type {String|Function|Object}
   * @description
   */
  footer: {
    type: [Object, Function, String],
    default: undefined,
  },

  /**
   * @type {String}
   * @description
   */
  content: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @description
   */
  programmatic: {
    type: Boolean,
    default: undefined,
  },

  /**
   * @type {Object}
   * @description Parent component of the modal, required if using component
   */
  parent: {
    type: Object,
    default: /* istanbul ignore next */ () => ({}),
  },

  parentClass: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @description Props to be binded to the injected component
   */
  props: {
    type: Object,
    default: undefined,
  },

  /**
   * @type {String}
   * @description Events to be binded to the injected component
   */
  events: {
    type: Object,
    default: undefined,
  },

  /**
   * @type {String}
   * @description Custom animation (transition name)
   */
  animation: {
    type: String,
    default: 'fade-down',
  },

  /**
   * @type {String}
   * @description Can close Modal by clicking 'X', pressing escape or
   * clicking outside
   */
  canCancel: {
    type: [Array, Boolean],
    default: /* istanbul ignore next */ () => {
      return config.defaultModalCanCancel
    },
  },

  /**
   * @type {String}
   * @description Callback function to call after user canceled (clicked
   * 'X' / pressed escape / clicked outside)
   */
  onCancel: {
    type: Function,
    default: /* istanbul ignore next */ () => {},
  },

  /**
   * @type {String}
   * @description Display modal as full screen
   */
  fullScreen: {
    type: Boolean,
    default: undefined,
  },

  /**
   * @type {String}
   * @description Trap focus inside the modal.
   */
  trapFocus: {
    type: Boolean,
    default: config.defaultTrapFocus,
  },

  /**
   * @type {String}
   * @description 	Role attribute to be passed to modal container for
   * better accessibility.
   */
  ariaRole: {
    type: String,
    default: 'dialog',
    validator: /* istanbul ignore next */ (value) => {
      return ['dialog', 'alertdialog'].indexOf(value) >= 0
    },
  },

  /**
   * @type {String}
   * @description Improve accessiblity when enabled.
   */
  ariaModal: {
    type: Boolean,
    default: undefined,
  },

  /**
   * @type {Function}
   * @description The submit method for the modal form
   */
  submitForm: {
    type: Function,
    default: undefined,
  },

  /**
   * @type {Boolean}
   * @description Determines if the modal body and footer should have
   * padding or not.
   */
  paddingless: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description Determines if the modal body will be display as flex, useful for tabs etc
   */
  flexBody: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description Determines if the modal will scroll inside the overlay or
   * the dialog itself scrolls
   */
  outerScroll: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Number}
   * @description Sets z-index of the modal
   */
  zIndex: {
    type: Number,
    default: 1013,
  },

  /**
   * @type {String}
   * @description Size of the modal dialog
   */
  size: {
    default: 'small',
    type: String,
    validator: /* istanbul ignore next */ (value) =>
      ['tiny', 'small', 'big', 'large', 'huge'].indexOf(value) >= 0,
  },
}

export const dialogProps = {
  /**
   * @type {String}
   * @description Dialog title
   */
  title: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @description Message text (can contain HTML).
   *
   * Dynamically rendering arbitrary HTML on your website can be very dangerous
   * because it can easily lead to XSS vulnerabilities. Only use HTML
   * interpolation on trusted content and never on user-provided content.
   */
  message: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @description Icon name if hasIcon, optional
   */
  icon: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @description Adds an icon on the left side depending on the type or icon
   */
  hasIcon: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {String}
   * @description
   */
  type: {
    type: String,
    default: 'primary',
    validator: /* istanbul ignore next */ (value) => {
      return ['primary', 'danger', 'warning', 'success'].indexOf(value) >= 0
    },
  },

  /**
   * @type {String}
   * @description Custom animation (transition name)
   */
  animation: {
    type: String,
    default: 'fade-down',
  },

  /**
   * @type {String}
   * @description Dialog's size, optional
   */
  size: {
    default: 'tiny',
    type: String,
    validator: /* istanbul ignore next */ (value) =>
      ['tiny', 'small', 'big', 'large', 'huge'].indexOf(value) >= 0,
  },

  /**
   * @type {String}
   * @description Text of the confirm button
   */
  confirmText: {
    type: String,
    default: /* istanbul ignore next */ () => {
      return config.defaultDialogConfirmText
        ? config.defaultDialogConfirmText
        : 'OK'
    },
  },

  /**
   * @type {String}
   * @description Text of the cancel button
   */
  cancelText: {
    type: String,
    default: /* istanbul ignore next */ () => {
      return config.defaultDialogCancelText
        ? config.defaultDialogCancelText
        : 'Cancel'
    },
  },

  /**
   * @type {String}
   * @description Determines if the dialog has an input
   */
  hasInput: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {String}
   * @description Prompt only: input's attributes
   */
  inputAttrs: {
    type: Object,
    default: /* istanbul ignore next */ () => ({}),
  },

  /**
   * @type {String}
   * @description Callback function when the confirm button is clicked
   */
  onConfirm: {
    type: Function,
    default: /* istanbul ignore next */ () => {},
  },

  /**
   * @type {String}
   * @description DOM element the dialog will be created on. Note that this
   * also changes the position of the dialog from fixed to absolute. Meaning
   * that the container should be fixed. Also note that this will override
   * the defaultContainerElement if you specified it in your options.
   */
  container: {
    type: String,
    default: config.defaultContainerElement,
  },

  /**
   * @type {String}
   * @description Focus on confirm or cancel button (when dialog is not prompt)
   */
  focusOn: {
    type: String,
    default: 'confirm',
  },

  /**
   * @type {String}
   * @description Trap focus inside the dialog.
   */
  trapFocus: {
    type: Boolean,
    default: config.defaultTrapFocus,
  },

  /**
   * @type {String}
   * @description 	Role attribute to be passed to modal container for
   * better accessibility.
   */
  ariaRole: {
    type: String,
    default: undefined,
    validator: /* istanbul ignore next */ (value) => {
      return ['dialog', 'alertdialog'].indexOf(value) >= 0
    },
  },

  /**
   * @type {String}
   * @description Improve accessiblity when enabled.
   */
  ariaModal: {
    type: Boolean,
    default: false,
  },
}

export const drawerProps = {
  /**
   * @type {String}
   * @description Dialog title
   */
  title: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @description Dialog's position
   */
  position: {
    type: String,
    default: 'right',
  },

  /**
   * @type {String}
   * @description DOM element the dialog will be created on. Note that this
   * also changes the position of the dialog from fixed to absolute. Meaning
   * that the container should be fixed. Also note that this will override
   * the defaultContainerElement if you specified it in your options.
   */
  container: {
    type: String,
    default: config.defaultContainerElement,
  },

  /**
   * @type {String}
   * @description decides whether to add some padding on the modal to inset it
   * from the left or right (useful for drawers in containers that need to look)
   * nested
   */
  inset: {
    type: String,
    default: null,
  },

  /**
   * @type {Number}
   * @description The drawer's maximum width in pixels. We use a max width so
   * it will stay responsive on smaller containers
   */
  width: {
    type: Number,
    default: 600,
  },

  minWidth: {
    type: [Number, String],
    default: null,
  },

  /**
   * @type {Boolean}
   * @description Improve accessiblity when enabled.
   */
  ariaModal: {
    type: Boolean,
    default: false,
  },

  /**
   * @type {Boolean}
   * @description has a backdrop or not
   */
  hasBackdrop: {
    type: Boolean,
    default: false,
  },

  showOverlay: {
    type: Boolean,
    default: false,
  },

  showHighest: {
    type: Boolean,
    default: false,
  },
  /**
   * @type {Boolean}
   * @description can close on click outside
   */
  clickClosesOutside: {
    type: Boolean,
    default: true,
  },

  /**
   * @type {Boolean}
   * @description show overflow or not
   */
  overflowShown: {
    type: Boolean,
    default: false,
  },
}

export const mediaManagerProps = {
  /**
   * @type {String}
   * @description The authentication token for making requests to
   * the akero API
   */
  apiKey: {
    type: String,
    default: undefined,
  },

  /**
   * @type {String}
   * @description The account we want to manage the images for
   */
  account: {
    default: undefined,
    type: [String, Object],
  },

  /**
   * @type {String}
   * @description The agency the account belongs too
   */
  agency: {
    default: undefined,
    type: [String, Object],
  },

  /**
   * @type {Function}
   * @description The callback for setting the requested image
   */
  callback: {
    default: undefined,
    type: Function,
  },

  /**
   * @type {String}
   * @description A legacy property to allow integration with the angular application
   * which relies on scope and indexes on the data to update the images.
   */
  index: {
    default: undefined,
    type: String,
  },

  /**
   * @type {Object}
   * @description A legacy property for the ng model in the angular application
   */
  ng: {
    default: undefined,
    type: Object,
  },

  /**
   * @type {String}
   * @description A legacy property to allow integration with the angular application
   * which relies on scope and indexes on the data to update the images.
   */
  scopeId: {
    default: undefined,
    type: String,
  },

  /**
   * @type {Object}
   * @description An object that may contain config options to show or hide certain parts
   * of the media manager.
   */
  hide: {
    default: undefined,
    type: Object,
  },

  /**
   * @type {Boolean}
   * @description Determines if the image options are displayed to the user
   */
  hideImageOpts: {
    default: false,
    type: Boolean,
  },

  /**
   * @type {Function}
   * @description A method that will show the support modal window when called.
   */
  showSupport: {
    default: undefined,
    type: Function,
  },
}
